// library
import { 
    FC, 
    useState, 
    useEffect, 
    useLayoutEffect, 
    useReducer, 
    useMemo, 
    useCallback,
    useRef,
} from "react";
import _ from "lodash";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
// leaflet
import { 
    MapContainer, 
    TileLayer, 
    useMap,
    LayersControl, 
    LayerGroup,
    Marker,
    ZoomControl,
    Popup
} from 'react-leaflet'
import L, { Layer, Marker as LeafletMarker } from 'leaflet';
import 'overlapping-marker-spiderfier-leaflet/dist/oms';
// material
import { Drawer, Typography } from "@mui/material";

// common
import Utils from "../common/utils";
import '../common/init-marker';
import { gpsIcon } from "../common/init-marker";
import { sightseeingIcon } from "../common/init-marker";
// components
import MapIconController from "./map-icon-controller";
import MapEventDialog from "./map-event-dialog";
import MapEventCard from "./map-event-card";
import Footer from "./footer";
import PrefectureDialog from "./prefecture-dialog";
// type
import { LatLng } from "leaflet";
import { FeatureCollection, Feature, GeoJSON } from "geojson";
import { Event } from "../types/event";
import { PrefecturePattern } from "../types/prefecture-pattern";
import { PrefectureCollection } from "../types/prefecture-collection";
import { Sightseeing } from "../types/sightseeing";
// data
import allEvent from "../data/events.json";
import getAdvertisement from "../common/advertisement";
// css
import "leaflet/dist/leaflet.css";

const { BaseLayer } = LayersControl;

const Map: FC<any> = ( 
    props: { 
        config: any, 
        handler: any, 
        isOpenPrefecture: boolean,
        setIsOpenPrefecture: any,
        setIsSettingDisplay: any,
        selectSightseeing: Sightseeing,
        setSelectSightseeing: any,
        selectPrefecture: number | null,
        setSelectPrefecture: any,
    }) =>{
    const { config,
            handler, 
            isOpenPrefecture, 
            setIsOpenPrefecture,
            setIsSettingDisplay,
            selectSightseeing,
            setSelectSightseeing,
            selectPrefecture,
            setSelectPrefecture,
        } = props;
    const [ mapCenter, setMapCenter ] = useState(new LatLng(36.7099904, 137.199616));
    const [ mapZoom, setMapZoom ] = useState(13);
    const [ events, setEvents ] = useState(allEvent as Event[]);
    const [ features, setFeatures ] = useState<Feature[]>([]);
    const [ preFeatureCollection, setPreFeatureCollection ] = useState<PrefectureCollection[]>([]);
    const [ isOpenMapEventDialog, setIsOpenMapEventDialog ] = useState<boolean>(false);
    const [ currentFeature, setCurrentFeature ] = useState<Feature | undefined>();
    const [ isPale, setIsPale ] = useState(true);
    const [ checkedKeys, setCheckedKeys ] = useState<string[]>([]);
    const [ isMoveMap, setIsMoveMap ] = useState<boolean>(false);
    const [ initMapCenter, setInitMapCenter ] = useState(new LatLng(0,0));
    const [ isCardDisplay, setIsCardDisplay ] = useState<boolean>(true);
    const [ prefectureName, setPrefectureName ] = useState<string>("");
    

    // イベント区分
    const EVENT_DIVISION = ['a', 'b', 'c', 'd']

    const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

    /**
     * 初期表示
     */
    useEffect(() => {

        // 実行
        initStart();

        // インターバルを削除
        return () => {
            if (intervalRef.current !== undefined) {
                clearInterval(intervalRef.current);
                intervalRef.current = undefined;
            }
        };

    }, []);


    /**
     * 初期表示
     */
    const initStart = async () => {
        try {
            // 都道府県コード
            let prefecture = 16;
            // 座標
            let latitude = 0;
            let longitude = 0;
            // 都道府県マスター
            const masterPrefecture = config.code_master.pref;

            // 地図表示ではheaderの設定マークは常に表示する
            setIsSettingDisplay(true);

            // 現在地の座標を取得
            const position = await getPosition();

            // 座標が返ってきた場合
            if (position) {
                // 都道府県コードと座標を設定
                latitude = position.coords.latitude;
                longitude = position.coords.longitude;
                prefecture = await reverseGeocoder({ lat: latitude, lon: longitude });

                // ★ここを追加
                setIsMoveMap(true);
                setMapCenter(new LatLng(latitude, longitude));
            }

            // 都道府県コードあり、都道府県と現在座標の都道府県コードが一致しない場合
            if (selectPrefecture && selectPrefecture !== prefecture) {

                // 都道府県選択からマスター情報取得
                const filterPosition = _.filter(masterPrefecture, (pre: any) => {
                    return pre.cd === selectPrefecture;
                });
                const position = _.head(filterPosition).position;
                
                // 都道府県コードと座標を設定
                latitude = position.coords.latitude;
                longitude = position.coords.longitude;
                prefecture = selectPrefecture;
            }

            // 選択都道府県に値がある場合
            if (selectPrefecture) {
                // 地図移動
                setIsMoveMap(true);
                if (selectSightseeing) {
                    // 観光に情報あり
                    const geometry: any = selectSightseeing.geometry;
                    const latLng = new LatLng(geometry.coordinates[1], geometry.coordinates[0]);
                    setMapCenter(latLng);
                } else {
                    // 観光に情報なし
                    const latLng = new LatLng(latitude, longitude);
                    setMapCenter(latLng);
                }
            }

            // 座標を地図中心に設定(Homeアイコン用)
            setInitMapCenter(new LatLng(latitude, longitude));

            // 都道府県をセット(観光に値を渡す為やむなく追記)
            setSelectPrefecture(prefecture);

            // 都道府県選択からマスター情報取得
            const filterPosition = _.filter(masterPrefecture, (pre: any) => {
                return pre.cd === prefecture;
            });
            const name = _.head(filterPosition).name;
            setPrefectureName(name);

            // initCallWrap
            const initCallWrap = () => {
                initCall(prefecture);
            }

            // 初回呼び出し
            initCallWrap();

            // intervalRef.currentを削除して、新しいものを設定
            if (intervalRef.current !== undefined) {
                clearInterval(intervalRef.current);
            }
            // インターバル実施(10分に一度更新)
            // intervalRef.current = setInterval(() => initCall(prefecture), 6000);
            intervalRef.current = setInterval(() => initCall(prefecture), 600000);

            // 座標からチェックボックス初期表示設定
            setCheckedKeys([
                String(prefecture),
                `${prefecture}-a`,
                `${prefecture}-b`,
                `${prefecture}-c`,
            ]);

        } catch (error) {
            console.error(error);
        }
    }

    /**
     * 位置情報の取得
     * @returns 
     */
    const getPosition = async (): Promise<any> => {

        const position = {
            coords: {
                latitude: 36.70203762015503,
                longitude: 137.21260987189558
            }
        }

        return new Promise<any>((resolve) => {
            navigator.geolocation.getCurrentPosition((pos) => {
                resolve({ coords: pos.coords });
            },
            (error) => {
                console.log("位置情報取得不可", error);
                resolve(position);
            });
        })
    }

    /**
     * 逆GoeJson
     * @param latLng 
     * @returns 
     */
    const reverseGeocoder = async (latLng: any): Promise<any> => {
        try {
            // 逆geoJsonリクエスト
            const res = await handler.map.getReverseGeocoder(config.reverseGeoJsonUrl, latLng);

            // 初期値
            let prefecture = 16;

            // 逆GeoJsonの値を変換して都道府県コードを取得
            if (res.data.results) {
                prefecture = Utils.muniCdToPrefecture(res.data.results.muniCd);
            }

            return prefecture;
        } catch (error) {
            console.error(error);
            // errorの時は16富山を表示
            return 16;
        }
    }

    /**
     * 初期呼び出し
     * @param prefecture 
     */
    const initCall = async (prefecture: number) => {

        // ローカルストレージのキー設定
        const storageTime = localStorage.getItem('time');
        const storageEvents = localStorage.getItem('events');
        const storagePrefecture = localStorage.getItem('prefecture');

        // 日付不一致・イベントが空・都道府県変更の場合
        if (Utils.getDate() !== storageTime || storageEvents === null || Number(storagePrefecture) !== prefecture) {
            // Eventリクエスト
            await requestEvents(prefecture, Utils.getDate());
        }
        // 再度イベント一覧取得
        const safeEvents = localStorage.getItem('events');

        // データが存在する場合は描画処理
        if (safeEvents) {
            // feature型に変換し保存
            const features: Feature[] = Utils.createFeatures(JSON.parse(safeEvents));
            setFeatures(features);
            // 画面描画
            convertData(features);
        }
    }

    /**
     * EVENTの呼び出し
     * 
     * @param prefecture 
     * @param today 
     */
    const requestEvents = async (prefecture: number, today: string): Promise<any> => {

        const event = _.head(_.filter(config.route, route => route.path === 'event')).url;
        const URL = `${config.baseUrl}${event}?prefecture=${prefecture}&endDate=${today}`;

        try {
            const data = await handler.map.doSearchEvents(URL);
            // データをイベント型に変換
            const events: Event[] = data === undefined ? [] : data;

            // 広告を表示
            _.map(getAdvertisement(), (advert: Event) => {
                // カテゴリー0は強制的にいれる
                if (advert.category === 0) {
                    advert.prefecture = prefecture;
                    events.push(advert);
                } else {
                    // カテゴリー0以外は都道府県が一致する場合にいれる
                    if (prefecture === advert.prefecture) {
                        events.push(advert);
                    }
                }
            });

            // イベントを並べ替え
            const sortData = _.sortBy(events, ['endDate', 'eventId']);
            // No付与・Coordinate補正
            _.map(sortData, (data: Event, index: number) => {
                Utils.latLngLegitimacy(data);
                return data;
            });

            // ローカルストレージに保存
            localStorage.setItem("events", JSON.stringify(sortData));
            localStorage.setItem("time", Utils.getDate());
            localStorage.setItem("prefecture", String(prefecture));
        } catch(error) {
            console.error(error);
        }
    };

    /**
     * データ形成
     * @param features 
     */
    const convertData = (features: Feature[]) => {
        // 都道府県別の空配列を定義
        const preFecturePattern:PrefecturePattern[] = [];
        const preFeatureCollection: PrefectureCollection[] = [];

        // 都道府県マスター取得
        const prefecture = config.code_master.pref;
        // 都道府県別空配列の初期化
        _.map(prefecture, (pref) => {
            preFecturePattern.push({ cd: pref.cd, featuresPattern: [ [], [], [], [] ] });
            preFeatureCollection.push({ cd: pref.cd, featureCollection: [] });
        });

        // 県別、開催時期別に仕分け
        _.map(preFecturePattern, (preFeature: PrefecturePattern) => {
            // 時期で仕分け
            _.map(features, (feature: Feature) => {
                if (preFeature.cd === feature.properties?.prefecture) {
                    const pattern = Utils.eventDatePattern(feature.properties?.startDate, feature.properties?.endDate);
                    if (pattern === 0) {
                        preFeature.featuresPattern[0].push(feature);
                    } else if (pattern === 1) {
                        preFeature.featuresPattern[1].push(feature);
                    } else if (pattern === 2) {
                        preFeature.featuresPattern[2].push(feature);
                    } else if (pattern === 3) {
                        preFeature.featuresPattern[3].push(feature);
                    }
                }
            });
        });

        // 県別、featureCollectionに格納
        _.map(preFeatureCollection, (collection: PrefectureCollection) => {
            // パターン分類の同じ県を抽出
            const featurePatterns = _.filter(preFecturePattern, (pattern: PrefecturePattern) => pattern.cd === collection.cd);
            const featurePattern = _.head(featurePatterns);
            // featureCollection型に変換して格納
            _.map(featurePattern?.featuresPattern, (featureP: Feature[]) => {
                collection.featureCollection.push({
                    type: "FeatureCollection",
                    features: featureP
                });
            });
        });

        // 都道府県別格納
        setPreFeatureCollection(preFeatureCollection);
    }

    /**
     * 地図移動
     * @returns 
     */
    const MoveCenter = () => {
        const map = useMap();        
        if (isMoveMap === true) {
            map.setView(mapCenter);
            setIsMoveMap(false);
        }
        return null;
    };

    /**
     *  ダイアログを開く
     * @param e 
     */
    const openMapEventDialog = (e: any) => {

        const eventId = e.target.feature.properties.eventId;
        const targetFeatures: any[] = _.filter(features, (feature: Feature) => ( feature.properties?.eventId === parseInt(eventId)))
        const targetFeature: any = _.head(targetFeatures);
        setCurrentFeature(targetFeature);
        // const latLng = new LatLng(targetFeature.geometry.coordinates[1], targetFeature.geometry.coordinates[0]);
        // setMapCenter(latLng);
        setIsOpenMapEventDialog(true);
        moveCard(targetFeature);
    }

    /**
     * カードクリック時イベント
     * @param feature 
     */
    const clickCard = (feature: any) => {
        // 地図を移動
        setIsMoveMap(true);
        const latLng = new LatLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
        setMapCenter(latLng);
        // setMapZoom(11);
        setCurrentFeature(feature);
        moveCard(feature);
    }

    /**
     * カードを移動
     * @param feature 
     */
    const moveCard = (feature: Feature) => {
        const cardWrapper = document.getElementById("card-wrapper");
        const eventCard = document.getElementById("event-card");
        
        const cardSize = eventCard?.offsetWidth; // カードサイズ
        const scrollSize =  cardWrapper?.scrollWidth; // 全体サイズ
        if (scrollSize && cardSize) {
            let scrollLeft = cardSize * feature.properties?.no + (feature.properties?.no * 5);
            if (feature.properties?.no === 0) {
                scrollLeft = 0;
            }
            cardWrapper.scrollLeft = scrollLeft;
        }
    }

    const DownPropsStyle = {
        width: 'auto',
        height: 'auto',
        maxHeight: '30vh',
        overflow: 'hidden',
        zIndex: '1000',
    }

    /**
     * ICON生成関数
     * @param latLng 
     * @param iconName 
     * @param className 
     * @returns 
     */
    const createIcon = (latLng: LatLng, iconName: string, className?: string) => Utils.createIcon(latLng, iconName, className);

    /**
     * geoJson生成
     * @param key 
     * @param featureCollection 
     * @param className 
     * @returns 
     */
    const createGeoJsonMap = (key: string, featureCollection: FeatureCollection, className?: string) => {
        const division = key.slice(-1);
        // アイコンの設定
        const pointToLayer = (feature: Feature, latLng: LatLng) => {
            if (feature.properties?.category === 0) {
                return;
            }

            const iconInfo = `marker-${feature.properties?.category}-${division}.svg`;
            const blankingClass = Utils.isBlanking(feature.properties as Event) ? 'blinking' : "";
            return createIcon(latLng, iconInfo, blankingClass);
        }
        // クリックイベント設定
        const onEachFeature = (feature: Feature, layer: Layer) => {
            // click時の動作設定
            const onClick: any = (event: React.MouseEvent<HTMLInputElement>) => {
                openMapEventDialog(event);
            }
            Utils.createClickEvent(layer, onClick);
        }

        key = key + new Date().getTime();
        return Utils.createGeoJson(key, featureCollection, pointToLayer, onEachFeature);
    }

    /**
     * 画面描画
     * @param list 
     * @returns 
     */
    const toDisplay = (list: any[]) => (key: string, creator: any, featureCollection: FeatureCollection, className?: string) => {
        if (!checkedKeys.includes(key)) return;
        list.push(creator(key, featureCollection, className))
    }

    // 描画配列
    let geoJsonList: any[] = [];

    // GeoJson生成処理
    const display = toDisplay(geoJsonList);

    // マーカー
    _.map(preFeatureCollection, (preCollection: PrefectureCollection) => {
        _.map(preCollection.featureCollection, (feature: FeatureCollection, index: number) => {
            display(`${preCollection.cd}-${EVENT_DIVISION[index]}`, createGeoJsonMap, feature);
        });
    });

    /**
     * カード表示
     */
    const cards = _.map(preFeatureCollection, (preCollection: PrefectureCollection) => {
        // 県毎に初期化
        let cardNoIndex = 0;
        return _.map(preCollection.featureCollection, (collection: FeatureCollection, index: number) => {
            if (_.includes(checkedKeys, `${preCollection.cd}-${EVENT_DIVISION[index]}`)) {
                return _.map(collection.features, (feature: Feature) => {
                    // cardにnoを付与(クリックした時に移動)
                    if (feature.properties) {
                        feature.properties.no = cardNoIndex;
                    }
                    cardNoIndex++;
                    // 描画
                    return(
                        <>
                            <MapEventCard
                                className="event-card"
                                config={config}
                                feature={feature}
                                currentFeature={currentFeature}
                                clickCard={clickCard}
                            />
                        </>
                    )
                });
            }
        });
    });

    /**
     * spiderFy
     * @param props 
     * @returns 
     */
    const SpiderFyComponent = (props: any) => {
        const { onSpiderfy, onUnspiderfy, onClick } = props;
        const map = useMap();
        useEffect(() => {
            const oms = new (window as any).OverlappingMarkerSpiderfier(map);
            oms.addListener("spiderfy", (markers: any[]) => {
                if (onSpiderfy) onSpiderfy(markers);
            });
            oms.addListener("unspiderfy", (markers: LeafletMarker) => {
                if (onUnspiderfy) onUnspiderfy(markers);
            });
            oms.addListener("click", (marker: LeafletMarker) => {
                if (onClick) onClick(marker);
            });
            map.on('layeradd', layerEvent => {
                if (layerEvent.layer instanceof L.Marker) {
                    oms.addMarker(layerEvent.layer);
                }
            });

        }, [map, onSpiderfy, onUnspiderfy, onClick]);
        return <LayerGroup>{ props.children }</LayerGroup>;
    }

    /**
     * spiderクリック時イベント
     * @param marker 
     */
    const handleOnClick = (marker: any) => {
        if (marker._events.clickMarker) {
            const event = { target: marker }
            marker._events.clickMarker[0].fn(event);
        }
    }

    /**
     * カードを閉じる
     */
    const cardWrapClose = () => {
        setIsCardDisplay(false);
    }

    /**
     * カードを開く
     */
    const cardWrapOpen = () => {
        setIsCardDisplay(true);
    }

    /**
     * ボタン
     * @param props 
     * @returns 
     */
    const ButtonControl: FC<any> = ( props: { className: string, text: string, onClick: any, iconName: string }) => {
        const { className, text, onClick, iconName } = props;
        return (
            <div className={'leaflet-bottom leaflet-left'}>
                <div>
                    <button 
                        className={className} 
                        type="button" 
                        onClick={onClick}
                    >
                        <img
                            className="card-display-icon"
                            src={`./icons/${iconName}.svg`}
                            width={25}
                            height={25}
                            alt="カード表示非表示アイコン"
                        />
                        <p
                            className="card-display-text"
                        >
                            {text}
                        </p>
                    </button>
                </div>
            </div>
        )
    }

    /**
     * 都道府県ボタン
     * @returns 
     */
    const PrefectureInfoButton: FC<any>  = () => {
        return (
            <div className={'leaflet-top'}>
                <Link 
                    to="sightseeing"
                    className="prefecture-button"
                    onClick={() => setIsSettingDisplay(false)}
                >
                    {`${prefectureName}の観光`}
                </Link>
            </div>
        )
    }

    /**
     * 観光マーカー
     * @returns 
     */
    const SightseeingMarker = () => {
        const geometry: any = selectSightseeing.geometry;
        const sightseeingPosition = new LatLng(geometry.coordinates[1], geometry.coordinates[0]);
        // googleMapへのリンク
        const googleMapsUrl = `${config.googleMapUrl}q=${geometry.coordinates[1]},${geometry.coordinates[0]}&z=15`;
        return (
            <Marker 
                position={sightseeingPosition}
                icon={sightseeingIcon}
            >
                <Popup>
                    <Typography variant="subtitle1">
                        {selectSightseeing.title}
                    </Typography>
                    <img 
                        src={`${config.localImageUrl}${selectSightseeing.image}.jpg`} 
                        alt={selectSightseeing.title} 
                        width="150"
                        style={{ display: "block", marginBottom: "8px" }}
                    />
                    <Typography gutterBottom>
                        <img
                            className="pictogram"
                            src="./icons/icon-link.svg"
                            height={20}
                        />
                        <a href={googleMapsUrl} target="_blank">Google Mapで開く</a>
                    </Typography>
                </Popup>
            </Marker>
        );
    }

    return (
        <>
            <Helmet>
                <title>イベント | 福井 石川 富山 北陸 | イベントを地図で確認</title>
                <meta name="description" content="今やってるイベントを地図ですぐにチェック! 出かけた先で近くで開催しているイベントを一目で確認できます。" />
                <meta property="og:title" content="イベント" />
                <meta property="og:description" content="福井・石川・富山・北陸のイベント情報を地図で確認できます！" />
                <meta property="og:url" content="https://eventamap.com" />
            </Helmet>
            <MapContainer 
                center={ mapCenter }
                zoom={ mapZoom } 
                zoomControl={false}
                className="map-container"
            >
                <ZoomControl
                    position="bottomright"
                />
                <LayersControl 
                    position="topleft"  
                    collapsed={false} 
                    hideSingleBase={true}
                >
                    <BaseLayer 
                        name="シンプル" 
                        checked={isPale}
                    >
                        <TileLayer
                            url="https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png"
                            attribution='<a href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>'
                        />
                    </BaseLayer>
                    <BaseLayer 
                        name="航空写真" 
                        checked={!isPale}
                    >
                        <TileLayer
                            url="https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"
                            attribution='<a href="https://maps.gsi.go.jp/development/ichiran.html">国土地理院</a>'
                        />
                    </BaseLayer>
                </LayersControl>
                
                {/* イベント時移動 */}
                <MoveCenter /> 

                {/* マーカー描画 */}
                <SpiderFyComponent 
                    onClick={handleOnClick}
                >
                    {_.map(geoJsonList, (value, index) => value)}
                </SpiderFyComponent>

                {/* GPSマーカー */}
                <Marker 
                    position={initMapCenter} 
                    icon={gpsIcon}
                />

                {/* 観光マーカー */}
                {(selectSightseeing && prefectureName) && (
                    <SightseeingMarker/>
                )}
                
            </MapContainer>
            {/* アイコンコントローラー */}
            <MapIconController
                pref={config.code_master.pref}
                events={events}
                checkedKeys={checkedKeys}
                setCheckedKeys={setCheckedKeys}
            >
            </MapIconController>
            {/* ダイアログ */}
            <MapEventDialog 
                config={config}
                isOpenDialog={isOpenMapEventDialog}
                setIsOpenDialog={setIsOpenMapEventDialog}
                currentFeature={currentFeature}
            ></MapEventDialog>
            {/* カード */}
            {isCardDisplay &&
                <Drawer
                    anchor="bottom"
                    open={true}
                    variant='persistent'
                    SlideProps={{style: DownPropsStyle}}
                >
                    <div 
                        className="card-wrapper"
                        id="card-wrapper"
                    >
                        {/* カード */}
                        {cards}
                    </div>
                </Drawer>
            }
            {isCardDisplay &&
                <ButtonControl
                    className="card-close-button"
                    text="カードを非表示"
                    onClick={cardWrapClose}
                    iconName="card-icon2"
                />
            }
            {!isCardDisplay &&
                <ButtonControl
                    className="card-open-button"
                    text="カードを表示"
                    onClick={cardWrapOpen}
                    iconName="card-icon1"
                />
            }
            {/* 都道府県情報ボタン */}
            <PrefectureInfoButton/>
            <Footer />
            {/* 都道府県選択ダイアログ */}
            <PrefectureDialog
                config={config}
                isOpenPrefecture={isOpenPrefecture}
                setIsOpenPrefecture={setIsOpenPrefecture}
                selectPrefecture={selectPrefecture}
                setSelectPrefecture={setSelectPrefecture}
                initStart={initStart}
            />
        </>
    );
}
export default Map;